import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "./icons.js";
import { Sheet } from "./sheet.js";
const KEY = "nozl-sites";
export function readSites(allowed) {
    try {
        const v = JSON.parse(sessionStorage.getItem(KEY) ?? "null");
        if (!Array.isArray(v))
            return null;
        const ok = v.filter((c) => typeof c === "string" && allowed.some((s) => s.code === c));
        return ok.length && ok.length < allowed.length ? ok : null;
    }
    catch {
        return null;
    }
}
export function saveSites(v) {
    try {
        if (v?.length)
            sessionStorage.setItem(KEY, JSON.stringify(v));
        else
            sessionStorage.removeItem(KEY);
    }
    catch { /* private window: just this page */ }
}
/** The session's site choice, limited to the sites given (call with the person's own trading sites). */
export function useSiteChoice(allowed) {
    const [v, setV] = useState(null);
    const key = allowed?.map((s) => s.code).join(",") ?? "";
    useEffect(() => { if (allowed)
        setV(readSites(allowed)); }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
    return [v, (n) => { const clean = n?.length && allowed && n.length < allowed.length ? n : null; saveSites(clean); setV(clean); }];
}
export const sitesLabel = (all, v) => !v?.length ? (all.length === 1 ? all[0].code : `All ${all.length} sites`) : v.length === 1 ? (all.find((s) => s.code === v[0])?.name ?? v[0]) : `${v.length} of ${all.length} sites`;
function usePhone() {
    const q = "(max-width: 700px)";
    const [m, setM] = useState(() => typeof window !== "undefined" && !!window.matchMedia?.(q).matches);
    useEffect(() => { const mq = window.matchMedia(q); const on = () => setM(mq.matches); mq.addEventListener("change", on); return () => mq.removeEventListener("change", on); }, []);
    return m;
}
export function SitePicker({ sites, value, onChange, className }) {
    const phone = usePhone();
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState(value);
    const timer = useRef(0);
    const btn = useRef(null);
    useEffect(() => { if (!open)
        setDraft(value); }, [value, open]);
    // Applies by itself a moment after the last tap, so ticking three sites loads once, not three times.
    const set = (v) => {
        const n = v?.length && v.length < sites.length ? v : null;
        setDraft(n);
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => onChange(n), 450);
    };
    const close = () => { window.clearTimeout(timer.current); if (JSON.stringify(draft) !== JSON.stringify(value))
        onChange(draft); setOpen(false); };
    useEffect(() => () => window.clearTimeout(timer.current), []);
    if (sites.length < 2)
        return null;
    const list = _jsx(SiteList, { sites: sites, value: draft, onChange: set });
    return (_jsxs(_Fragment, { children: [_jsxs("button", { ref: btn, type: "button", className: `tc-sitebtn tc-dw-sitebtn${className ? ` ${className}` : ""}`, "aria-haspopup": "dialog", "aria-expanded": open, onClick: () => (open ? close() : setOpen(true)), children: [_jsx(Icon, { name: "pin", size: 16 }), " ", _jsx("span", { className: "tc-sp-lbl", children: sitesLabel(sites, value) }), " ", _jsx(Icon, { name: "chevron-down", size: 14 })] }), phone
                ? _jsx(Sheet, { open: open, onClose: close, title: "Sites", foot: _jsx("button", { type: "button", className: "tc-sp-done", onClick: close, children: "Done" }), children: list })
                : open && _jsx(Popover, { anchor: btn.current, onClose: close, children: list })] }));
}
/** The list itself: All sites, then each site with a tick and "Only". A search box once there are more than eight. */
export function SiteList({ sites, value, onChange }) {
    const [q, setQ] = useState("");
    const shown = useMemo(() => { const t = q.trim().toLowerCase(); return t ? sites.filter((s) => `${s.code} ${s.name}`.toLowerCase().includes(t)) : sites; }, [q, sites]);
    const all = !value?.length;
    return (_jsxs("div", { className: "tc-sp", role: "group", "aria-label": "Choose sites", children: [sites.length > 8 && (_jsxs("label", { className: "tc-sp-search", children: [_jsx(Icon, { name: "search", size: 16 }), _jsx("input", { className: "tc-input", value: q, onChange: (e) => setQ(e.target.value), placeholder: "Find a site", "aria-label": "Find a site" })] })), _jsxs("button", { type: "button", className: `tc-sp-row all${all ? " on" : ""}`, "aria-pressed": all, onClick: () => onChange(null), children: [_jsx("span", { className: "tc-sp-tick", "aria-hidden": "true", children: all && _jsx(Icon, { name: "check", size: 14 }) }), _jsxs("span", { className: "tc-sp-t", children: [_jsx("b", { children: "All sites" }), _jsxs("small", { children: [sites.length, " sites"] })] })] }), shown.map((s) => {
                const on = !!value?.includes(s.code);
                return (_jsxs("div", { className: `tc-sp-row${on ? " on" : ""}`, children: [_jsxs("button", { type: "button", className: "tc-sp-pick", "aria-pressed": on, onClick: () => onChange(on ? (value ?? []).filter((x) => x !== s.code) : [...(value ?? []), s.code]), children: [_jsx("span", { className: "tc-sp-tick", "aria-hidden": "true", children: on && _jsx(Icon, { name: "check", size: 14 }) }), _jsxs("span", { className: "tc-sp-t", children: [_jsx("b", { children: s.name }), _jsx("small", { children: s.code })] })] }), _jsx("button", { type: "button", className: "tc-sp-only", onClick: () => onChange([s.code]), "aria-label": `Only ${s.name}`, children: "Only" })] }, s.code));
            }), !shown.length && _jsxs("p", { className: "tc-sp-none", children: ["No site matches \"", q, "\"."] })] }));
}
/** A popover on document.body under its button (kept on screen), closed by a tap outside or Escape. */
function Popover({ anchor, onClose, children }) {
    const [pos, setPos] = useState(null);
    const box = useRef(null);
    const close = useRef(onClose);
    close.current = onClose;
    useLayoutEffect(() => {
        const place = () => {
            if (!anchor)
                return;
            const r = anchor.getBoundingClientRect(), w = 340;
            setPos({ top: r.bottom + 8, left: Math.max(12, Math.min(r.left, window.innerWidth - w - 12)), maxH: Math.max(220, window.innerHeight - r.bottom - 24) });
        };
        place();
        window.addEventListener("resize", place);
        window.addEventListener("scroll", place, true);
        return () => { window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); };
    }, [anchor]);
    useEffect(() => {
        const down = (e) => { if (!box.current?.contains(e.target) && !anchor?.contains(e.target))
            close.current(); };
        const key = (e) => { if (e.key === "Escape")
            close.current(); };
        document.addEventListener("pointerdown", down);
        document.addEventListener("keydown", key);
        return () => { document.removeEventListener("pointerdown", down); document.removeEventListener("keydown", key); };
    }, [anchor]);
    if (!pos)
        return null;
    return createPortal(_jsx("div", { ref: box, className: "tc-sp-pop", role: "dialog", "aria-label": "Sites", style: { top: pos.top, left: pos.left, maxHeight: pos.maxH }, children: children }), document.body);
}
/**
 * ONE SITE AT A TIME (CRS, rent, a rota…): the same look as the picker, a chip with the site's name that opens the
 * list (a sheet from the bottom on a phone, a popover elsewhere). Tap a site and it's chosen. `allLabel` adds an
 * "All sites" row (value "").
 */
export function SiteSwitch({ sites, value, onChange, allLabel, label = "Site", className }) {
    const phone = usePhone();
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const btn = useRef(null);
    const sorted = useMemo(() => [...sites].sort((a, b) => a.name.localeCompare(b.name)), [sites]);
    const shown = useMemo(() => { const t = q.trim().toLowerCase(); return t ? sorted.filter((s) => `${s.code} ${s.name}`.toLowerCase().includes(t)) : sorted; }, [q, sorted]);
    if (sites.length < 2 && !allLabel)
        return null;
    const here = sites.find((s) => s.code === value);
    const pick = (code) => { setOpen(false); setQ(""); if (code !== value)
        onChange(code); };
    const row = (code, name, small) => (_jsxs("button", { type: "button", className: `tc-sp-row tc-sp-one${code === value ? " on" : ""}${code ? "" : " all"}`, "aria-pressed": code === value, onClick: () => pick(code), children: [_jsxs("span", { className: "tc-sp-t", children: [_jsx("b", { children: name }), _jsx("small", { children: small })] }), code === value && _jsx(Icon, { name: "check", size: 18 })] }, code || "all"));
    const list = (_jsxs("div", { className: "tc-sp", role: "group", "aria-label": `Choose a ${label.toLowerCase()}`, children: [sites.length > 8 && _jsxs("label", { className: "tc-sp-search", children: [_jsx(Icon, { name: "search", size: 16 }), _jsx("input", { className: "tc-input", value: q, onChange: (e) => setQ(e.target.value), placeholder: "Find a site", "aria-label": "Find a site" })] }), allLabel && !q && row("", allLabel, `${sites.length} sites`), shown.map((s) => row(s.code, s.name, s.code)), !shown.length && _jsxs("p", { className: "tc-sp-none", children: ["No site matches \"", q, "\"."] })] }));
    return (_jsxs(_Fragment, { children: [_jsxs("button", { ref: btn, type: "button", className: `tc-sitebtn${className ? ` ${className}` : ""}`, "aria-haspopup": "dialog", "aria-expanded": open, "aria-label": `${label}: ${here?.name ?? allLabel ?? "choose"}`, onClick: () => setOpen(!open), children: [_jsx(Icon, { name: "pin", size: 16 }), " ", _jsx("span", { className: "tc-sp-lbl", children: here?.name ?? allLabel ?? "Choose a site" }), " ", _jsx(Icon, { name: "chevron-down", size: 14 })] }), phone ? _jsx(Sheet, { open: open, onClose: () => setOpen(false), title: label === "Site" ? "Sites" : label, children: list }) : open && _jsx(Popover, { anchor: btn.current, onClose: () => setOpen(false), children: list })] }));
}
